import { ApolloError } from '@apollo/client';
import {
  captureException,
  DssAnalyticsContext,
  GameHighlights,
  HeroSection,
  LanguageContext,
  PlayerPerformance,
  SelectSidebar,
  TransactionsModal,
} from '@dapperlabs/core-fe';
import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useSession } from 'src/edge/session';
import { ContentPresenter } from 'src/general/components/common/ContentPresenter';
import Head from 'src/general/components/Head/Head';
import { MomentCirculation } from 'src/general/components/MomentCirculation';
import { SalesHistory } from 'src/general/components/SalesHistory';
import { useSerialSidebar, useTransactionsModal } from 'src/general/hooks';
import { useMomentAnalytics } from 'src/general/hooks/useMomentAnalytics';
import { getMomentData } from 'src/general/utils/moments';
import { addApolloState, createApolloClient } from 'src/lib/apollo';
import { useFeatureFlags } from 'src/lib/launchDarkly';
import { useTranslation } from 'src/lib/translations';
import LaligaColorLogo from 'src/modules/Layout/assets/png/laliga_logo_color.png';
import {
  GET_PLAY,
  SEARCH_AGGREGATION,
  searchAggregationByEditionID,
} from 'src/modules/Queries';
import {
  searchEditionAggregationData,
  searchEditionSalesHistory,
} from 'src/modules/Queries/editionQueries';
import {
  SearchGolazosNftAggregationQuery,
  SearchGolazosNftAggregationQueryVariables,
} from 'src/modules/Queries/searchAggregation.generated';
import { SEARCH_GOLAZOS_EDITIONS_BY_EDITION_IDS } from 'src/modules/Queries/searchNft';
import { NonEmptyString } from 'src/modules/Queries/utilTypes';
import { Box, breakpoints, Flex, useMediaQuery } from 'src/theme';

type EditionProps = {
  aggregationData: {
    forSaleCount: number;
    hiddenCount: number;
    ownedCount: number;
    reservedCount: number;
  };
  edition: any;
  editionID: any;
  editionPrice: any;
  error: ApolloError | null;
  merchandising: any;
  play: any;
  playDataID: string;
  salesHistory: {
    latestSales: any;
    topSales: any;
  };
};
const Edition = ({
  error,
  edition,
  play,
  playDataID,
  aggregationData,
  salesHistory,
  editionID,
  editionPrice,
}: EditionProps) => {
  const {
    state: { context },
    send,
  } = useSession();

  const { isMarketplaceEnabled } = useFeatureFlags();
  const [selectedNft, setSelectedNft] = useState({
    id: '',
    listing: {
      listing_resource_id: '',
      price: '',
    },
    owner_address: '',
    serial_number: '',
  });
  const languages = useMemo(
    () => [
      { label: 'English', urlValue: '_default', value: 'en' },
      { label: 'Español', urlValue: '_es', value: 'es' },
    ],
    [],
  );
  const { t } = useTranslation();
  const [isMdUp] = useMediaQuery(`screen and (min-width: ${breakpoints.md})`);

  const moment = getMomentData({
    aggregationData,
    languages,
    moment: { edition },
    play: play?.getPlay?.play,
    playDataID,
    salesHistory,
    showMobileVideos: !isMdUp,
    t,
  });

  const analytics = useMomentAnalytics({ edition });

  const [videoLanguage, setVideoLanguage] = useState(i18next.language);
  const [globalLanguage, setGlobalLanguage] = useState(i18next.language);

  i18next.on('languageChanged', (lang) => {
    setVideoLanguage(lang);
    setGlobalLanguage(lang);
  });

  const handleLanguage = useMemo(
    () => ({
      languages,
      onChangeLanguage: ({ value }) => setVideoLanguage(value),
      selectedLanguage: languages.find((lang) => lang.value === videoLanguage),
    }),
    [videoLanguage, languages],
  );

  const flowAddress = useMemo(
    () => context?.profile?.flowAddress?.replace(/0x/g, ''),
    [context.profile],
  );
  const isLoggedIn = useMemo(() => Boolean(flowAddress), [flowAddress]);

  const isOwnerNFT = useMemo(
    () => flowAddress === selectedNft.owner_address,
    [flowAddress, selectedNft.owner_address],
  );
  const {
    state,
    setState,
    stage,
    setStage,
    typeModal,
    transactionStage,
    txId,
    setTransactionStage,
    additionalButtonHandler,
    prices,
    submitModal,
    isOpen,
    setIsOpen,
    heroSectionButton,
  } = useTransactionsModal({
    flowAddress,
    imageUrl: moment.carouselItems[videoLanguage]?.[1]?.src,
    isLoggedIn,
    isOwnerNFT,
    momentNFT: selectedNft,
    nftID: selectedNft.id,
    price: editionPrice,
  });

  const isAuthed = useMemo(
    () => context?.profile?.flowAddress,
    [context?.profile?.flowAddress],
  );

  const {
    sort,
    setSort,
    editionList,
    sortOptions,
    btnLabel,
    setSelectedEditionId,
    selectedEditionId,
    setIsOpenBrowseListingModal,
    isOpenBrowseListingModal,
    disabledBtn,
  } = useSerialSidebar({
    editionID,
    isAuthed,
    preferredCurrency: context.preferredCurrency,
  });

  useEffect(() => {
    const selectedNFT = editionList[selectedEditionId];
    if (selectedNFT) {
      const price = selectedNFT.askingPrice.replace(/[^\d]/g, '');
      setState({
        ...state,
        price,
      });
      setSelectedNft({
        id: selectedNFT.nft_id,
        listing: {
          listing_resource_id: selectedNFT.resourceID,
          price: String(Number(price) * 100000000),
        },
        owner_address: selectedNFT.owner_address,
        serial_number: selectedNFT.serial,
      });
    }
  }, [selectedEditionId]);

  const handleSerialSidebar = () => {
    if (!isAuthed) {
      return send('SIGN_IN');
    }
    if (typeof heroSectionButton !== 'boolean') {
      isLoggedIn && heroSectionButton?.callback();
    }
  };

  const metadata = moment.moment.type.split(', ');

  const momentInfo = useMemo(
    () => ({
      amountMomentsBurned: moment.burnedSize,
      currentPosition: selectedNft.serial_number,
      nameFootballer: moment.moment.title,
      rarity: moment.rarityType,
      tags: metadata,
      totalAmountMoments: moment.editionSize,
    }),
    [
      metadata,
      moment.burnedSize,
      moment.editionSize,
      moment.moment.title,
      moment.rarityType,
      selectedNft.serial_number,
    ],
  );

  const isOwnerSelectedNft = useMemo(
    () => selectedNft.owner_address.includes(flowAddress),
    [selectedNft, flowAddress],
  );

  return (
    <>
      <Head
        description={t('Head.EditionPage.Description', {
          playDescription: moment.summary[globalLanguage],
        })}
        openGraphImage={moment.heroImage[globalLanguage]}
        subtitle={t('Head.EditionPage.Subtitle', {
          playType: momentInfo.tags[0],
          playerName: momentInfo.nameFootballer,
        })}
      />
      <SelectSidebar
        btnLabel={btnLabel}
        disabledBtn={disabledBtn || isOwnerSelectedNft}
        editionInfo={{
          editionSize: moment.editionSize,
          name: moment.name,
          rarityType: moment.rarityType,
          src: moment?.momentMainVideo?.previewSrc,
        }}
        editionList={editionList}
        flowAddress={flowAddress}
        isOpen={isOpenBrowseListingModal}
        onClick={handleSerialSidebar}
        onSelectItem={(index) => setSelectedEditionId(index)}
        selectedItemIndex={selectedEditionId}
        setIsOpenBrowseListingModal={setIsOpenBrowseListingModal}
        setSort={setSort}
        sort={sort}
        sortOptions={sortOptions}
      />
      <TransactionsModal
        additionalButtonHandler={additionalButtonHandler}
        imageSrc={moment.carouselItems[videoLanguage]?.[1]?.src}
        isOpen={isOpen}
        momentInfo={momentInfo}
        prices={prices}
        setIsOpen={setIsOpen}
        setStage={setStage}
        setState={setState}
        setTransactionStage={setTransactionStage}
        stage={stage}
        state={state}
        submitModal={submitModal}
        transactionStage={transactionStage}
        txId={txId}
        typeModal={typeModal}
      />
      <ContentPresenter error={error}>
        <DssAnalyticsContext.Provider value={analytics}>
          <Flex justifyContent='center' paddingX='1rem'>
            <Box maxWidth='1224px' paddingY='2rem'>
              <LanguageContext.Provider value={handleLanguage}>
                <HeroSection
                  avgPrice={editionPrice?.avg}
                  burnedSize={moment.burnedSize}
                  buttonLabel={t('HeroSection.button.browseListings')}
                  carouselItems={moment.carouselItems[videoLanguage]}
                  countForSale={editionPrice?.count}
                  currency={{ suffix: 'USD' }}
                  editionSize={moment.editionSize}
                  isShowBtn={
                    Boolean(isMarketplaceEnabled) && editionList.length > 0
                  }
                  isShowForSale
                  lowestPrice={editionPrice?.min}
                  moment={moment.moment}
                  onClick={() => {
                    setIsOpenBrowseListingModal(!isOpenBrowseListingModal);
                  }}
                  rarityType={moment.rarityType}
                  summary={moment.summary[globalLanguage]}
                  totalBurned={moment.totalBurned}
                />
              </LanguageContext.Provider>
            </Box>
          </Flex>
          <Box bgColor='rgba(23,23,21,0.4)' paddingX='1rem'>
            <Box maxWidth='1224px' margin='0 auto' paddingY='3.75rem'>
              <GameHighlights
                duration={90}
                logo={LaligaColorLogo}
                playData={moment}
              />
              <PlayerPerformance
                paddingTop={{ base: '3rem', sm: '5rem' }}
                paddingBottom='3rem'
                player={moment}
                season={moment.season}
              />
              <MomentCirculation
                sx={{
                  borderTop: '1px solid #2B2C33',
                  margin: '0 auto',
                  maxWidth: '1224px',
                  paddingY: '6.5rem',
                }}
                circulationId='moment-details'
                {...moment}
              />
              <SalesHistory
                editionId={moment.editionId}
                latestSales={moment.latestSales}
                topSales={moment.topSales}
              />
            </Box>
          </Box>
        </DssAnalyticsContext.Provider>
      </ContentPresenter>
    </>
  );
};

// Fetch moment details info from the server to render metadata
export async function getServerSideProps(context) {
  const client = createApolloClient();
  const { id: editionID } = context.query;
  if (!editionID) {
    throw new Error('Edition id not provided');
  }

  const editionPromise = client.query({
    context: {
      capturePolicy: 'none',
      clientName: 'platformAPI',
    },
    query: SEARCH_GOLAZOS_EDITIONS_BY_EDITION_IDS([
      editionID as NonEmptyString<string>,
    ]),
  });

  const aggregationPromise = client.query<
    SearchGolazosNftAggregationQuery,
    SearchGolazosNftAggregationQueryVariables
  >({
    context: {
      clientName: 'platformAPI',
      req: context.req,
    },
    query: SEARCH_AGGREGATION,
    variables: searchAggregationByEditionID(
      editionID as NonEmptyString<string>,
    ),
  });

  const initialPromises = await Promise.allSettled([
    editionPromise,
    aggregationPromise,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [edition, aggregation] = initialPromises.map((promise) => {
    if (promise.status === 'fulfilled') return promise.value;
    if (promise.status === 'rejected')
      captureException(promise.reason, { req: context.req, res: context.res });
    return null;
  });

  const key = Object.keys(edition.data)[0];

  const playDataID =
    edition.data[key]?.edges?.[0]?.node?.edition?.play.metadata.play_data_id;
  const price =
    aggregation?.data?.searchGolazosNftAggregation?.edges?.[0]?.node?.listing
      ?.price;

  const aggregationQueries = searchEditionAggregationData(
    client,
    editionID as NonEmptyString<string>,
  );
  const salesHistoryQueries = searchEditionSalesHistory(
    client,
    editionID as NonEmptyString<string>,
  );

  const otherPromises = await Promise.allSettled([
    client.query({
      context: {
        capturePolicy: 'none',
        clientName: 'laligaAPI',
        req: context.req,
      },
      query: GET_PLAY,
      variables: { playID: playDataID },
    }),
    aggregationQueries.hiddenCount,
    aggregationQueries.reservedCount,
    aggregationQueries.ownedCount,
    aggregationQueries.forSaleCount,
    salesHistoryQueries.topSales,
    salesHistoryQueries.latestSales,
  ]);

  const [
    play,
    hiddenMoments,
    reservedMoments,
    ownedMoments,
    forSaleMoments,
    topSales,
    latestSales,
  ] = otherPromises.map((promise) => {
    if (promise.status === 'fulfilled') return promise.value;
    if (promise.status === 'rejected') captureException(promise.reason);
    return null;
  });

  const DENOMINATOR = 100000000;

  const historySales =
    latestSales?.data?.searchGolazosMarketplaceHistory?.edges || [];

  const totalSales = historySales.length;

  const sum = historySales.reduce((acc, curr) => {
    const nodePrice = parseInt(curr.node.price);

    return acc + nodePrice;
  }, 0);

  const avgPrice = totalSales > 0 ? sum / totalSales / DENOMINATOR : 0;

  const editionPrice = {
    avg: avgPrice,
    count:
      forSaleMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node?.id
        ?.count || 0,
    max:
      topSales?.data?.searchGolazosMarketplaceHistory?.edges?.[0]?.node?.price /
        DENOMINATOR || 0,
    min: price?.min / DENOMINATOR || 0,
  };

  return addApolloState(client, {
    props: {
      aggregation:
        aggregation?.data?.searchGolazosNftAggregation?.edges?.[0]?.node ||
        null,
      aggregationData: {
        forSaleCount:
          forSaleMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node
            ?.id?.count || 0,
        hiddenCount:
          hiddenMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node?.id
            ?.count || 0,
        ownedCount:
          ownedMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node?.id
            ?.count || 0,
        reservedCount:
          reservedMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node
            ?.id?.count || 0,
      },
      edition: edition.data[key]?.edges?.[0]?.node?.edition,
      editionID,
      editionPrice: editionPrice ?? null,
      error: aggregation?.errors || play?.errors || null,
      play: play?.data || null,
      playDataID: playDataID || null,
      salesHistory: {
        latestSales: {
          count:
            latestSales?.data?.searchGolazosMarketplaceHistory?.totalCount || 0,
          data: latestSales?.data?.searchGolazosMarketplaceHistory?.edges || [],
        },
        topSales: {
          count:
            topSales?.data?.searchGolazosMarketplaceHistory?.totalCount || 0,
          data: topSales?.data?.searchGolazosMarketplaceHistory?.edges || [],
        },
      },
    },
  });
}

export default Edition;
